


















































































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { Card, Button, Icon, TextInput } from '@alpha/ink'

import Redactor from '@/components/Redactor.vue'

import ComparisonList from '@/components/AddTest/ComparisonList.vue'
import ConceptAsset from '@/components/AddTest/ConceptAsset.vue'
import * as db from '@shared/types/database'
import * as graph from '@shared/types/graphql'

import * as ah from '@/utilities/asset-helpers'
import analytics from '@/analytics'
import ClientUploadDisplays from '@/components/ClientUploads/ClientUploadDisplays.vue'
import ClientUploadUpdateList from '@/components/ClientUploads/ClientUploadUpdateList.vue'

interface EditableData {
  title: string
  prototypes: Record<string, db.Prototype>
  description: string
  additional_info: string // eslint-disable-line
  uploads: Record<string, db.Prototype>
  conceptAssets: Record<string, db.Prototype>
  comparisonOptions: Record<string, db.Prototype>
}

@Component({
  components: {
    ClientUploadDisplays,
    ClientUploadUpdateList,
    Card,
    Button,
    Redactor,
    Icon,
    ComparisonList,
    ConceptAsset,
    TextInput
  }
})

export default class ClassicTestContent extends Vue {
  @Prop() test!: db.Test
  @Prop({ default: false }) readonly editMode!: Boolean
  @Prop({ default: false }) readonly saveSubmitted!: Boolean
  @Prop({ default: false }) readonly showCustomerQuestions!: Boolean

  shown = {
    title: true,
    concept: true,
    description: true,
    webpage: true,
    comparison: true,
    additionalInfo: true,
    customerQuestions: true,
    uploads: true
  }

  editableData = this.createEditableData(this.test.payload)
  deletedAssetKeys: string[] = []

  @Watch('test')
  onTestChanged (test: db.Test) {
    this.editableData = this.createEditableData(test.payload)
  }

  @Watch('editableData', { deep: true })
  onEditableDataChanged (editableData: EditableData) {
    const testPayloadUpdate: Partial<db.TestPayload> = {
      title: editableData.title,
      description: editableData.description,
      additional_info: editableData.additional_info,
      prototypes: {
        ...editableData.uploads,
        ...editableData.comparisonOptions,
        ...editableData.conceptAssets
      }
    }
    this.$emit('onTestPayloadChanged', testPayloadUpdate)
  }

  get hasDescription (): boolean {
    // Descriptions were turned on accidentally for some test types that don't have it
    // so we don't want to hide user input data
    if (this.editableData.description && this.editableData.description !== '') return true
    const testTypes = ['Comparison', 'Feature Prioritization', 'Usability Testing']
    return testTypes.includes(this.test.payload.testType)
  }

  toggleShown (key: keyof ClassicTestContent['shown']) {
    this.shown[key] = !this.shown[key]
  }

  updateConceptAssets (assets: Record<string, db.Prototype>) {
    Object.keys(assets).forEach(key => {
      const asset = assets[key]
      asset.conceptPrototype = true
    })
    this.editableData.conceptAssets = assets
  }

  updateComparisonOptions (options: Record<string, db.Prototype>) {
    Object.keys(options).forEach(key => {
      const asset = options[key]
      asset.comparisonPrototype = true
    })
    this.editableData.comparisonOptions = options
  }

  editUpload (upload: graph.Prototype) {
    this.editableData.uploads = ah.editUpload(this.editableData.uploads, upload)
  }

  addUploads (uploads: Record<string, db.Prototype>) {
    this.editableData.uploads = ah.addUploads(this.editableData.uploads, uploads)
    analytics.track('Assets Uploaded', { testId: this.test.id })
  }

  removeUpload (assetKey: string) {
    this.editableData.uploads = ah.removeUpload(this.editableData.uploads, assetKey)
    this.$emit('onAssetDeleted', assetKey)
  }

  areUploadsValid (areUploadsValid: boolean) {
    this.$emit('areUploadsValid', areUploadsValid)
  }

  get uploads () {
    return ah.formatDbPrototypes(this.editableData.uploads)
  }

  filterConceptAssets (prototypes: Record<string, db.Prototype>) {
    return Object.keys(prototypes).reduce((acc, key) => {
      const prototype = prototypes[key]
      if (prototype.conceptPrototype || prototype.conceptPrototypeDesignRequest) {
        acc[key] = prototype
      }
      return acc
    }, {} as Record<string, db.Prototype>)
  }

  filterComparisonOptions (prototypes: Record<string, db.Prototype>) {
    return Object.keys(prototypes).reduce((acc, key) => {
      const prototype = prototypes[key]
      if (prototype.comparisonPrototype) {
        acc[key] = prototype
      }
      return acc
    }, {} as Record<string, db.Prototype>)
  }

  filterUploads (prototypes: Record<string, db.Prototype>) {
    return Object.keys(prototypes).reduce((acc, key) => {
      const prototype = prototypes[key]
      if (!prototype.conceptPrototype && !prototype.comparisonPrototype) {
        acc[key] = prototype
      }
      return acc
    }, {} as Record<string, db.Prototype>)
  }

  createEditableData (payload: db.TestPayload): EditableData {
    return {
      title: payload.title ?? '',
      prototypes: Object.assign({}, payload.prototypes),
      description: payload.description,
      additional_info: payload.additional_info!,
      uploads: this.filterUploads(payload.prototypes || {}),
      conceptAssets: this.filterConceptAssets(payload.prototypes || {}),
      comparisonOptions: this.filterComparisonOptions(payload.prototypes || {})
    }
  }
}
